import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Avatar
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { GetStoreOverviewLayout, GetUsersForStore, UserConfigCreation, BaysForSingleStore } from '../../../../api/index';
import { useSelector } from 'react-redux';
import Tooltip from '@mui/material/Tooltip';
// import PersonIcon from '@mui/icons-material/Person';

// Agent data will be fetched from API

const AgentBayAssignment = () => {
  const { store_id } = useParams();
  const [layoutData, setLayoutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [savingAssignments, setSavingAssignments] = useState(false);
  const [bays, setBays] = useState([]);
  const imageRef = useRef(null);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [assignmentMode, setAssignmentMode] = useState(false);
  // Store all assignments in a format: { bayId: agentId }
  const [assignedBays, setAssignedBays] = useState({});
  // Store assignments by category for each agent
  const [agentAssignments, setAgentAssignments] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const selectedDate = useSelector((state) => state.customization.selectedDate)
    .toISOString()
    .slice(0, 10);

  const getStoreOverviewData = async () => {
    setLoading(true);
    try {
      // Ensure we're using the correct store_id format from params
      const rawStoreId = localStorage.getItem('analysisStoreId');
      const storeId = store_id || rawStoreId?.replace(/"/g, '');
      console.log('Fetching store layout with store_id:', storeId);

      const response = await GetStoreOverviewLayout({
        dates: selectedDate,
        store_id: storeId
      });

      if (response && response.data) {
        console.log('Store layout data:', response.data);
        setLayoutData(response.data);

        // Check if we have the expected data structure
        if (response.data.data && response.data.data[0] && response.data.data[0].store_config) {
          const storeConfig = response.data.data[0].store_config;
          console.log('Store config:', storeConfig);

          // Create an array to hold all bays with their category information
          const processedBays = [];

          // Process each category and its bays
          storeConfig.forEach((category) => {
            if (Array.isArray(category.bays)) {
              // Add category information to each bay
              const baysWithCategory = category.bays.map((bay) => ({
                ...bay,
                category_name: category.category_name || bay.type,
                category_id: category.category_id
              }));

              // No longer filtering out bays with name="filled" in metadata
              // Add all bays to our processed array
              processedBays.push(...baysWithCategory);
            }
          });

          console.log('Processed bays with categories:', processedBays.length);
          setBays(processedBays);
        } else {
          console.error('Unexpected data structure:', response.data);
          setBays([]);
        }
      } else {
        console.error('Error: No data available');
      }
    } catch (error) {
      console.error('Error fetching store overview layout:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAgentsData = async () => {
    setLoadingAgents(true);
    try {
      // Ensure we're using the correct store_id format
      const rawStoreId = localStorage.getItem('analysisStoreId');
      const storeId = store_id || rawStoreId?.replace(/"/g, '');
      console.log('Fetching agents with store_id:', storeId);

      const response = await GetUsersForStore({
        store_id: storeId
      });

      if (response && response.data && response.data.data) {
        console.log('Agents data:', response.data.data);

        // Transform the API response to our required format
        const formattedAgents = response.data.data.map((user) => ({
          id: user._id,
          userId: user.user_id,
          name: user.user_name,
          role: user.user_role,
          department: user.user_dept,
          email: user.email,
          assignedBays: []
        }));

        setAgents(formattedAgents);

        // TODO: Fetch existing bay assignments for each agent
        // For now, we'll initialize with empty assignments
        const initialAssignments = {};
        formattedAgents.forEach((agent) => {
          initialAssignments[agent.id] = [];
        });
        setAgentAssignments(initialAssignments);
      } else {
        console.error('Error: No agents data available');
        setAgents([]);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  const fetchAssignedBays = async () => {
    try {
      // Ensure we're using the correct store_id format
      const rawStoreId = localStorage.getItem('analysisStoreId');
      const storeId = store_id || rawStoreId?.replace(/"/g, '');

      const response = await BaysForSingleStore({
        store_id: storeId,
        date: selectedDate
      });

      if (response && response.data && response.data.data) {
        console.log('Assigned bays data:', response.data.data);

        // Initialize new assigned bays object
        const newAssignedBays = {};
        const newAgentAssignments = {};

        // Process each user's assignments
        response.data.data.forEach((userData) => {
          const agentId = agents.find((agent) => agent.userId === userData.user_id)?.id;

          if (agentId) {
            // Initialize agent assignments if not exists
            if (!newAgentAssignments[agentId]) {
              newAgentAssignments[agentId] = [];
            }

            // Process each category assignment
            userData.assignments.forEach((assignment) => {
              // Add category to agent assignments
              newAgentAssignments[agentId].push({
                category: assignment.category_name,
                bays: assignment.bays
              });

              // Add each bay to the assignedBays mapping
              assignment.bays.forEach((bayId) => {
                newAssignedBays[bayId] = agentId;
              });
            });
          }
        });

        // Update state with fetched assignments
        setAssignedBays(newAssignedBays);
        setAgentAssignments((prevAssignments) => ({
          ...prevAssignments,
          ...newAgentAssignments
        }));
      }
    } catch (error) {
      console.error('Error fetching assigned bays:', error);
    }
  };

  useEffect(() => {
    getStoreOverviewData();
    getAgentsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store_id, selectedDate]);

  // Fetch assigned bays after agents are loaded
  useEffect(() => {
    if (agents.length > 0 && !loadingAgents) {
      fetchAssignedBays();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
    setAssignmentMode(true);
  };
  useEffect(() => {
    setAgents((prev) =>
      prev.map((agent) => {
        const updatedAssignedBays = Object.entries(assignedBays)
          .filter(([agentId]) => agentId === agent.id)
          .map(([bayId]) => bayId);

        return { ...agent, assignedBays: updatedAssignedBays };
      })
    );
  }, [assignedBays]);

  const handleBayClick = (bayId) => {
    if (assignmentMode && selectedAgent) {
      const clickedBay = bays.find((bay) => bay._id === bayId);
      if (!clickedBay) return;

      // Toggle bay assignment for the selected agent
      setAssignedBays((prev) => {
        const newAssignedBays = { ...prev };

        // If this bay is already assigned to this agent, remove it
        if (newAssignedBays[bayId] === selectedAgent.id) {
          delete newAssignedBays[bayId];
        } else {
          // Otherwise assign it to this agent
          newAssignedBays[bayId] = selectedAgent.id;
        }

        return newAssignedBays;
      });

      // Update agent assignments by category
      setAgentAssignments((prev) => {
        const newAssignments = { ...prev };
        const categoryName = clickedBay.category_name || clickedBay.type;

        // Initialize agent's assignments if not exists
        if (!newAssignments[selectedAgent.id]) {
          newAssignments[selectedAgent.id] = [];
        }

        // Find if there's already an entry for this category
        const categoryIndex = newAssignments[selectedAgent.id].findIndex((item) => item.category === categoryName);

        // Check if we're assigning or unassigning by looking at the previous state
        // This is crucial because assignedBays hasn't been updated yet when this code runs
        const isAssigning = prev[bayId] !== selectedAgent.id;
        // If we're assigning the bay
        if (isAssigning) {
          // If category exists in assignments
          if (categoryIndex >= 0) {
            // Add bay to existing category if not already there
            if (!newAssignments[selectedAgent.id][categoryIndex].bays.includes(bayId)) {
              newAssignments[selectedAgent.id][categoryIndex].bays.push(bayId);
            }
          } else {
            // Create new category entry
            newAssignments[selectedAgent.id].push({
              category: categoryName,
              bays: [bayId]
            });
          }
        } else {
          // If we're unassigning a bay and the category exists
          if (categoryIndex >= 0) {
            // Remove bay from category
            newAssignments[selectedAgent.id][categoryIndex].bays = newAssignments[selectedAgent.id][categoryIndex].bays.filter(
              (id) => id !== bayId
            );

            // If category has no bays left, remove the category
            if (newAssignments[selectedAgent.id][categoryIndex].bays.length === 0) {
              newAssignments[selectedAgent.id].splice(categoryIndex, 1);
            }
          }
        }

        return newAssignments;
      });
    }
  };

  const handleCancelAssignment = () => {
    setSelectedAgent(null);
    setAssignmentMode(false);
  };

  const handleSaveAssignments = async () => {
    if (!selectedAgent) return;

    setSavingAssignments(true);
    try {
      // Format the data according to the required structure
      const payload = {
        id: selectedAgent.userId, // Using user_id as required by the API
        timestamp: new Date().toISOString(),
        assignments: agentAssignments[selectedAgent.id] || []
      };

      console.log('Saving assignments with payload:', payload);

      // Call the API to save the assignments
      const response = await UserConfigCreation(payload);

      if (response && response.data) {
        console.log('Assignments saved successfully:', response.data);
        // Exit assignment mode
        setSelectedAgent(null);
        setAssignmentMode(false);
      } else {
        console.error('Error saving assignments: No response data');
      }
    } catch (error) {
      console.error('Error saving assignments:', error);
    } finally {
      setSavingAssignments(false);
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase();
  };

  const getRandomColor = (id) => {
    const colors = ['#FFD700', '#FFA500', '#87CEEB', '#90EE90', '#FFC0CB', '#ADD8E6', '#98FB98', '#DDA0DD', '#FFDAB9', '#B0E0E6'];
    return colors[id % colors.length];
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        <Typography variant="h4" sx={{ fontSize: isMobile ? '1.5rem' : '2.125rem' }}>
          Agent Bay Assignment
        </Typography>

        <Typography variant="subtitle1" color="text.secondary">
          Store: {layoutData?.data?.[0]?.name || store_id}
        </Typography>

        {/* <Typography variant="body2" color="text.secondary">
          Assign store bays to agents by selecting an agent and then clicking on bays in the store layout.
        </Typography> */}
      </Box>

      {/* Assignment Controls - only visible when an agent is selected */}
      {selectedAgent && assignmentMode && (
        <Box
          sx={{
            mb: 2,
            p: isMobile ? 1 : 2,
            backgroundColor: `${theme.palette.primary.light}30`,
            borderRadius: 1,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            gap: isMobile ? 1 : 0
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                mr: 2,
                bgcolor: getRandomColor(selectedAgent.id),
                width: 40,
                height: 40,
                color: 'black'
              }}
            >
              {getInitials(selectedAgent.name)}
            </Avatar>
            <Typography>
              Assigning bays to <strong>{selectedAgent.name}</strong>
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              width: isMobile ? '100%' : 'auto',
              justifyContent: isMobile ? 'space-between' : 'flex-end',
              mt: isMobile ? 1 : 0
            }}
          >
            <Button
              variant="outlined"
              color="error"
              size={isMobile ? 'small' : 'medium'}
              sx={{ mr: 1 }}
              onClick={handleCancelAssignment}
              disabled={savingAssignments}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size={isMobile ? 'small' : 'medium'}
              onClick={handleSaveAssignments}
              disabled={savingAssignments}
              startIcon={savingAssignments ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {savingAssignments ? 'Saving...' : 'Save Assignments'}
            </Button>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: isTablet ? 'column' : 'row',
          gap: 3,
          height: isTablet ? 'auto' : 'calc(100vh - 200px)'
        }}
      >
        {/* Agent List */}
        <Paper
          elevation={3}
          sx={{
            width: isTablet ? '100%' : '300px',
            height: isTablet ? '300px' : '100%',
            overflow: 'auto',
            flex: isTablet ? 'none' : '0 0 300px'
          }}
        >
          <Typography
            variant="h6"
            sx={{
              p: 2,
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <span>Agents</span>
            {loadingAgents && <CircularProgress size={20} sx={{ color: 'white' }} />}
          </Typography>

          <List>
            {agents.length > 0
              ? agents.map((agent) => (
                  <React.Fragment key={agent.id}>
                    <ListItem
                      button
                      selected={selectedAgent?.id === agent.id}
                      onClick={() => handleAgentSelect(agent)}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: `${theme.palette.primary.light}30`
                        }
                      }}
                    >
                      <Avatar
                        sx={{
                          mr: 2,
                          bgcolor: getRandomColor(agent.id),
                          width: 40,
                          height: 40,
                          color: 'black'
                        }}
                      >
                        {getInitials(agent.name)}
                      </Avatar>
                      <ListItemText
                        primary={agent.name}
                        secondary={`${agent.role} (${agent.userId})`}
                        primaryTypographyProps={{
                          fontWeight: selectedAgent?.id === agent.id ? 'bold' : 'normal'
                        }}
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))
              : !loadingAgents && (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No agents found for this store
                    </Typography>
                  </Box>
                )}
          </List>
        </Paper>

        {/* Store Layout */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            height: isTablet ? '500px' : '100%',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: '#f5f5f5'
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : layoutData?.data && layoutData?.data[0]?.layout_image ? (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                overflow: 'auto',
                flex: 1
              }}
            >
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  height: 'auto',
                  display: 'inline-block',
                  touchAction: 'none'
                }}
              >
                <img
                  ref={imageRef}
                  src={layoutData?.data[0]?.layout_image}
                  alt="Store Layout"
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain'
                  }}
                />
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                  {bays.map((bay) => {
                    const topPercent = bay.coordinates?.y || 0;
                    const leftPercent = bay.coordinates?.x || 0;
                    const isAssigned = assignedBays[bay._id] !== undefined;
                    const isAssignedToSelected = assignedBays[bay._id] === selectedAgent?.id;

                    return (
                      <div
                        key={bay._id}
                        style={{
                          position: 'absolute',
                          top: `${topPercent}%`,
                          left: `${leftPercent}%`,
                          transform: 'translate(-50%, -50%)',
                          zIndex: 2,
                          pointerEvents: 'auto'
                        }}
                      >
                        <Tooltip
                          title={
                            <div style={{ padding: '4px 8px' }}>
                              <div>
                                <strong>Bay ID:</strong> {bay.bay_id || bay._id}
                              </div>
                              <div>
                                <strong>Fixture:</strong> {bay.fixture_name || 'N/A'}
                              </div>
                              <div>
                                <strong>Category:</strong> {bay.category_name || bay.type || 'N/A'}
                              </div>
                              {isAssigned && (
                                <div>
                                  <strong>Assigned to:</strong> {agents.find((a) => a.id === assignedBays[bay._id])?.name}
                                </div>
                              )}
                            </div>
                          }
                          arrow
                          placement="bottom"
                        >
                          <Button
                            variant={isAssignedToSelected ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => handleBayClick(bay._id)}
                            style={{
                              backgroundColor: isAssigned
                                ? isAssignedToSelected
                                  ? theme.palette.primary.main
                                  : selectedAgent && assignedBays[bay._id] !== selectedAgent.id
                                  ? '#9e9e9e' // Gray for bays assigned to other agents
                                  : getRandomColor(assignedBays[bay._id])
                                : '#f5f5f5',
                              minWidth: isMobile ? '24px' : '32px',
                              width: isMobile ? '24px' : '32px',
                              height: isMobile ? '24px' : '32px',
                              borderRadius: '50%',
                              padding: 0,
                              border: '2px solid white',
                              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
                              transition: 'all 0.3s ease',
                              cursor: assignmentMode ? 'pointer' : 'default',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {isAssigned ? (
                              <Typography variant="caption" sx={{ fontSize: 12, color: 'white', fontWeight: 'bold' }}>
                                {getInitials(agents.find((a) => a.id === assignedBays[bay._id])?.name || '')}
                              </Typography>
                            ) : (
                              <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#9e9e9e' }}></span>
                            )}
                          </Button>
                        </Tooltip>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="h6" color="text.secondary">
                No store layout available
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AgentBayAssignment;
